import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Target, Layers, Trophy, ShieldCheck, ArrowUpRight, Star } from "lucide-react";
import * as LI from "lucide-react";
import { SERVICES } from "@/data/site";
import { useEffect, useState } from "react";
import { listBlog, listLogos } from "@/lib/api";
import Seo, { ORG_SCHEMA, SITE_URL } from "@/components/Seo";
import { useSite } from "@/lib/site";

const iconMap = {
  megaphone: LI.Megaphone, monitor: LI.Monitor, kanban: LI.KanbanSquare,
  "share-2": LI.Share2, palette: LI.Palette, lightbulb: LI.Lightbulb,
  shield: LI.ShieldCheck, "graduation-cap": LI.GraduationCap,
};

const HERO_SLIDES = SERVICES.slice(0, 5);

export default function Home() {
  const { site } = useSite();
  const [slide, setSlide] = useState(0);
  const [posts, setPosts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    listBlog().then((d) => setPosts(d.slice(0, 3))).catch(() => {});
  }, []);

  useEffect(() => {
    listLogos("partner").then(setPartners).catch(() => {});
    listLogos("client").then(setClients).catch(() => {});
  }, []);

  const active = HERO_SLIDES[slide];

  return (
    <div data-testid="home-page">
      <Seo
        title="Home"
        description="Digi Hub Solution — Best-in-class IT, Digital Marketing, Web Design, IT Security & Business consultation services. Established 2018, based in Rishikesh, Uttarakhand."
        image="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1200&auto=format&fit=crop"
        jsonLd={[ORG_SCHEMA, { "@context": "https://schema.org", "@type": "WebSite", name: "Digi Hub Solution", url: SITE_URL }]}
      />
      {/* HERO */}
      <section className="relative hero-gradient text-white overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="container-x relative py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="chip-light reveal d1">
              <Sparkles className="w-3.5 h-3.5" /> WE SERVE IN
            </div>
            <h1 key={active.slug} className="reveal d2 mt-5 text-4xl md:text-6xl leading-[1.05]" data-testid="hero-title">
              {active.title.split(" ").map((w, i) => (
                <span key={`${active.slug}-w-${i}`} className={i % 2 === 0 ? "" : "text-[color:var(--dh-orange)]"}>{w} </span>
              ))}
            </h1>
            <p key={active.slug + "-p"} className="reveal d3 mt-5 text-white/80 text-lg max-w-xl leading-relaxed">
              {active.long}
            </p>
            <div className="reveal d4 mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-contact">Contact Us <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/services" className="btn-outline" data-testid="hero-cta-services">Explore Services</Link>
            </div>

            {/* Slider dots */}
            <div className="reveal d5 mt-10 flex items-center gap-2" data-testid="hero-dots">
              {HERO_SLIDES.map((s, i) => (
                <button key={s.slug} onClick={() => setSlide(i)} data-testid={`hero-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${i === slide ? "w-10 bg-[color:var(--dh-orange)]" : "w-5 bg-white/30"}`} />
              ))}
            </div>
          </div>

          <div className="relative reveal d3">
            <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-[color:var(--dh-orange)]/30 blur-2xl" />
            <div className="relative rounded-[28px] overflow-hidden border border-white/15 shadow-2xl">
              <img src={active.image} alt={active.title} className="w-full h-[420px] object-cover" />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                <div className="chip-light">Featured</div>
                <div className="mt-2 font-display text-2xl">{active.title}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative container-x pb-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white/5 border border-white/10 rounded-2xl backdrop-blur p-6" data-testid="hero-stats">
            {(site.hero?.stats || [
              { n: "6+", l: "Years in Business" },
              { n: "120+", l: "Projects Delivered" },
              { n: "45+", l: "Happy Clients" },
              { n: "24/7", l: "Support & Care" },
            ]).map((s) => (
              <div key={s.l} className="text-center md:text-left">
                <div className="font-display text-3xl md:text-4xl text-white">{s.n}</div>
                <div className="text-sm text-white/60 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / INTRO */}
      <section className="container-x py-20" data-testid="home-about">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow">DIGI HUB SOLUTIONS</div>
            <h2 className="mt-3 text-3xl md:text-5xl text-[color:var(--dh-navy)]">
              Your all-in-one partner for <span className="text-[color:var(--dh-orange)]">growth & security.</span>
            </h2>
            <p className="mt-5 text-[color:var(--dh-slate)] leading-relaxed">
              Digi Hub Solution is here to provide best-class consultation services relevant to IT, Business, Digital Media and Security. We are a multi-service company that acts as your business promoter — from smart IT projects and cyber security to digital marketing and web development.
            </p>
            <p className="mt-4 text-[color:var(--dh-slate)] leading-relaxed">
              Our IT consultation gives you a clear path to identify technology requirements and cost-effective solutions. We deal in IT management, setup, networking (voice & data VOIP), IT security services, website design and hosting.
            </p>
            <Link to="/about" className="btn-ghost-dark mt-7" data-testid="home-about-cta">About Us <ArrowRight className="w-4 h-4" /></Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { i: Target, t: "Tailored Solutions", d: "Strategies that fit your goals, not templates." },
              { i: Trophy, t: "Proven Expertise", d: "Marketers, strategists & IT consultants." },
              { i: Layers, t: "End-to-End", d: "From concept to launch, we handle it all." },
              { i: ShieldCheck, t: "Security First", d: "IT security, cyber and network protection." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="p-5 rounded-2xl border border-[color:var(--dh-line)] hover:border-[color:var(--dh-orange)] transition-colors bg-white">
                <div className="w-11 h-11 rounded-xl bg-[color:var(--dh-navy)] text-white grid place-items-center"><Icon className="w-5 h-5" /></div>
                <div className="mt-3 font-display font-bold text-[color:var(--dh-navy)]">{t}</div>
                <div className="text-sm text-[color:var(--dh-slate)] mt-1">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS marquee */}
      <section className="py-10 bg-[color:var(--dh-cream)] border-y border-[color:var(--dh-line)]" data-testid="partners-strip">
        <div className="container-x text-center">
          <div className="eyebrow justify-center">Our Partners</div>
        </div>
        <div className="mt-6 overflow-hidden">
          <div className="marquee-track flex gap-14 min-w-[200%] items-center">
            {partners.length > 0 && [...partners, ...partners, ...partners].map((p, i) => (
              <img key={`${p.id || p.name}-${i}`} src={p.src} alt={p.name} title={p.name} className="h-16 w-auto object-contain rounded-lg opacity-80 hover:opacity-100 transition" />
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x py-20" data-testid="home-services">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow">Our Services</div>
            <h2 className="mt-3 text-3xl md:text-5xl text-[color:var(--dh-navy)]">Everything you need. <span className="text-[color:var(--dh-orange)]">Under one roof.</span></h2>
          </div>
          <Link to="/services" className="btn-ghost-dark" data-testid="home-services-cta">View All <ArrowUpRight className="w-4 h-4" /></Link>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.slice(0, 6).map((s) => {
            const Icon = iconMap[s.icon] || LI.Sparkles;
            return (
              <div key={s.slug} className="svc-card" data-testid={`service-card-${s.slug}`}>
                <div className="w-12 h-12 rounded-xl bg-[color:var(--dh-orange)]/15 text-[color:var(--dh-orange)] grid place-items-center"><Icon className="w-6 h-6" /></div>
                <h3 className="mt-4 font-display text-xl text-[color:var(--dh-navy)]">{s.title}</h3>
                <p className="mt-2 text-[color:var(--dh-slate)] text-sm leading-relaxed relative z-10">{s.short}</p>
                <Link to={`/services/${s.slug}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--dh-navy)] hover:text-[color:var(--dh-orange)] relative z-10">
                  Learn more <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* CLIENTS marquee */}
      <section className="py-16 bg-white" data-testid="home-clients">
        <div className="container-x text-center">
          <div className="eyebrow justify-center">Our Clients</div>
          <h2 className="mt-3 text-3xl md:text-4xl text-[color:var(--dh-navy)]">Trusted by growing brands</h2>
        </div>
        <div className="mt-10 overflow-hidden" data-testid="clients-marquee">
          <div className="marquee-track flex gap-14 min-w-[200%] items-center">
            {clients.length > 0 && [...clients, ...clients, ...clients, ...clients].map((c, i) => (
              <img
                key={`${c.id || c.name}-${i}`}
                src={c.src}
                alt={c.name}
                title={c.name}
                className="h-16 w-auto object-contain rounded-lg opacity-80 hover:opacity-100 transition"
              />
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[color:var(--dh-navy)] text-white py-20" data-testid="testimonials">
        <div className="container-x">
          <div className="grid md:grid-cols-3 gap-6">
            {(site.testimonials || []).map((t) => (
              <div key={t.name} className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur">
                <div className="flex gap-0.5 text-[color:var(--dh-orange-2)]">{[...Array(5)].map((_, i) => <Star key={`${t.name}-star-${i}`} className="w-4 h-4 fill-current" />)}</div>
                <p className="mt-4 text-white/85 leading-relaxed">"{t.quote}"</p>
                <div className="mt-5">
                  <div className="font-display font-bold">{t.name}</div>
                  <div className="text-sm text-white/60">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT POSTS */}
      <section className="container-x py-20" data-testid="home-blog">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow">Recent Posts</div>
            <h2 className="mt-3 text-3xl md:text-5xl text-[color:var(--dh-navy)]">From the blog</h2>
          </div>
          <Link to="/blog" className="btn-ghost-dark">All Posts <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link to={`/blog/${p.slug}`} key={p.slug} className="post-card group block rounded-2xl overflow-hidden border border-[color:var(--dh-line)] bg-white" data-testid={`home-post-${p.slug}`}>
              <div className="overflow-hidden aspect-[16/10]">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="chip">{p.category}</div>
                <h3 className="mt-3 font-display text-lg text-[color:var(--dh-navy)] group-hover:text-[color:var(--dh-orange)] transition-colors">{p.title}</h3>
                <div className="mt-2 text-sm text-[color:var(--dh-slate)]">{p.date}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-20">
        <div className="rounded-3xl overflow-hidden relative p-10 md:p-14 bg-[color:var(--dh-orange)] text-white">
          <div className="absolute inset-0 opacity-20 grid-bg" />
          <div className="relative grid md:grid-cols-[1.5fr_auto] items-center gap-6">
            <div>
              <h2 className="text-3xl md:text-5xl leading-[1.05]">Ready to grow your business?</h2>
              <p className="mt-3 text-white/90 max-w-xl">Book a free 30-minute consultation. We'll review your website, socials and IT setup — and share a growth plan.</p>
            </div>
            <Link to="/contact" className="btn-outline bg-white/10 hover:bg-white hover:text-[color:var(--dh-orange)]" data-testid="footer-cta">Book a Free Call <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
